import { useCallback, useEffect, useState } from "react";
import { Rocket, RotateCw, CheckCircle2, AlertTriangle, Clock, Loader2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const STATUS = {
  not_started: { label: "Waiting for launch", cls: "text-sage", Icon: Clock },
  queued: { label: "Queued", cls: "text-gold", Icon: Clock },
  provisioning: { label: "Setting up…", cls: "text-gold", Icon: Loader2 },
  done: { label: "Live", cls: "text-forest", Icon: CheckCircle2 },
  waiting_bank: { label: "Waiting on bank config", cls: "text-clay", Icon: AlertTriangle },
  error: { label: "Failed", cls: "text-clay", Icon: AlertTriangle },
};

function withPin() {
  let pin = sessionStorage.getItem("admin_pin") || "";
  if (!pin) {
    pin = window.prompt("Enter Admin PIN to confirm this action:") || "";
    if (!pin) return null;
    sessionStorage.setItem("admin_pin", pin);
  }
  return { headers: { "X-Admin-PIN": pin } };
}

export default function LaunchPanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get("/admin/launch").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const act = async (path, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    const cfg = withPin(); if (!cfg) return;
    setBusy(true); setMsg("");
    try { const { data } = await api.post(path, {}, cfg); setD((prev) => ({ ...prev, ...data })); await load(); }
    catch (e) { if (e.response?.status === 401) sessionStorage.removeItem("admin_pin"); setMsg(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!d) return null;
  const live = d.status === "live";
  const prov = d.provisioning || {};
  const failed = (prov.error || 0) + (prov.waiting_bank || 0);

  return (
    <div className="mt-8 bg-linen border border-sand rounded-2xl p-6" data-testid="launch-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-sage"><Rocket className="w-3 h-3" /> Launch</div>
          <h2 className="font-serif text-2xl mt-1.5" data-testid="launch-headline">
            {live ? "We're live." : `${d.preorders_paid.toLocaleString()} of ${d.target.toLocaleString()} Founding Members`}
          </h2>
          <p className="text-xs text-moss mt-1 max-w-xl">
            {live
              ? `Launched ${new Date(d.launched_at).toLocaleString()} (${d.triggered_by}). Every paid preorderer leaves Demo and gets real Spending / Savings / Bills accounts + cards, then an email.`
              : `At ${d.target.toLocaleString()} paid preorders the real version unlocks automatically and every Founding Member's accounts are set up. Everyone else stays in Demo until they subscribe.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!live && (
            <button onClick={() => act("/admin/launch/now", `Launch now with ${d.preorders_paid} preorders? This creates real bank accounts for every Founding Member and cannot be undone.`)} disabled={busy}
              className="inline-flex items-center gap-1.5 bg-forest text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-forest-deep disabled:opacity-60" data-testid="launch-now-btn">
              <Rocket className="w-4 h-4" strokeWidth={2} /> Launch now
            </button>
          )}
          {live && failed > 0 && (
            <button onClick={() => act("/admin/launch/retry-failed")} disabled={busy}
              className="inline-flex items-center gap-1.5 border border-sand rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-sand-light disabled:opacity-60" data-testid="launch-retry-btn">
              <RotateCw className="w-4 h-4" /> Retry {failed} failed
            </button>
          )}
        </div>
      </div>

      <div className="mt-5">
        <div className="flex justify-between text-[11px] text-sage mb-1.5"><span>{d.percent}% to launch</span><span>{live ? "target reached" : `${d.remaining.toLocaleString()} to go`}</span></div>
        <div className="h-2.5 rounded-full bg-sand overflow-hidden" data-testid="launch-progress">
          <div className={`h-full rounded-full transition-[width] duration-700 ${live ? "bg-forest" : "bg-gold"}`} style={{ width: `${live ? 100 : Math.max(1.5, d.percent)}%` }} />
        </div>
      </div>

      {live && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-center" data-testid="launch-provisioning-stats">
          {["queued", "provisioning", "done", "waiting_bank", "error"].map((k) => (
            <div key={k} className="bg-cream border border-sand rounded-lg py-3">
              <div className="font-mono text-xl">{prov[k] || 0}</div>
              <div className={`text-[10px] uppercase tracking-wider ${STATUS[k].cls}`}>{STATUS[k].label}</div>
            </div>
          ))}
        </div>
      )}
      {msg && <div className="mt-3 text-sm text-clay" data-testid="launch-error">{msg}</div>}

      <details className="mt-5 group" data-testid="launch-members">
        <summary className="cursor-pointer text-xs font-medium text-moss hover:text-ink">Founding Members ({d.members.length})</summary>
        <ul className="mt-3 divide-y divide-sand border-t border-sand max-h-72 overflow-auto">
          {d.members.map((m) => { const s = STATUS[m.provision?.status] || STATUS.not_started; const I = s.Icon; return (
            <li key={m.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`launch-member-${m.id}`}>
              <div className="min-w-0"><div className="truncate">{m.name || m.email}</div><div className="text-[11px] text-sage truncate">{m.email} · preordered {m.preorder_paid_at ? new Date(m.preorder_paid_at).toLocaleDateString() : "—"}</div></div>
              <div className={`shrink-0 inline-flex items-center gap-1 text-xs ${s.cls}`} title={m.provision?.error || ""}><I className={`w-3.5 h-3.5 ${m.provision?.status === "provisioning" ? "animate-spin" : ""}`} /> {s.label}</div>
            </li>); })}
          {d.members.length === 0 && <li className="py-3 text-xs text-sage">No paid preorders yet.</li>}
        </ul>
      </details>
    </div>
  );
}
